"use client";

import Link from "next/link";
import { useState, type ReactNode } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

export function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-8 pb-10 border-b border-gray-100 last:border-0">
      <h2 className="text-xl font-bold text-gray-900 mb-1">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function normalizeLang(lang: string): string {
  const map: Record<string, string> = {
    py: "python",
    js: "javascript",
    ts: "typescript",
    sh: "bash",
    shell: "bash",
    yml: "yaml",
    dockerfile: "docker",
  };
  return map[lang.toLowerCase()] ?? lang.toLowerCase();
}

export function CodeBlock({ code, lang = "python", label }: { code: string; lang?: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const normalized = normalizeLang(lang);
  const trimmed = code.replace(/\n$/, "");

  const copy = () => {
    navigator.clipboard.writeText(trimmed).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-gray-950 border-b border-gray-800">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <span className="text-xs text-gray-500 font-mono ml-1">{label ?? normalized}</span>
        <button
          onClick={copy}
          className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
            copied ? "text-green-400" : "text-gray-400 hover:text-gray-200"
          }`}
          aria-label="Copy code"
        >
          {copied ? (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={normalized}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          padding: "1rem 1.25rem",
          backgroundColor: "#0a0a0a",
          fontSize: "0.875rem",
          lineHeight: "1.65",
          borderRadius: 0,
          overflowX: "auto",
        }}
        codeTagProps={{
          style: {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            fontSize: "inherit",
            backgroundColor: "transparent",
          },
        }}
      >
        {trimmed}
      </SyntaxHighlighter>
    </div>
  );
}

export function Param({ name, type, required, desc }: { name: string; type: string; required?: boolean; desc: ReactNode }) {
  return (
    <div className="flex gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="w-48 flex-shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <code className="text-sm font-mono font-semibold text-gray-900">{name}</code>
          {required && <span className="text-xs text-red-600 font-medium">required</span>}
        </div>
        <div className="text-xs text-gray-500 font-mono mt-0.5 break-words">{type}</div>
      </div>
      <div className="flex-1 text-sm text-gray-600 leading-relaxed">{desc}</div>
    </div>
  );
}

export function ParamTable({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      {title && (
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</span>
        </div>
      )}
      <div className="px-4">{children}</div>
    </div>
  );
}

export function Callout({ type, title, children }: { type: "warn" | "info" | "tip"; title?: string; children: ReactNode }) {
  const cfg = {
    warn: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", iconColor: "text-amber-600" },
    info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", iconColor: "text-blue-600" },
    tip:  { bg: "bg-green-50", border: "border-green-200", text: "text-green-800", iconColor: "text-green-600" },
  }[type];
  return (
    <div className={`p-4 rounded-xl ${cfg.bg} border ${cfg.border}`}>
      {title && (
        <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${cfg.iconColor}`}>{title}</div>
      )}
      <div className={`text-sm leading-relaxed ${cfg.text}`}>{children}</div>
    </div>
  );
}

export function ApiBreadcrumb({ lang }: { lang?: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm font-mono text-gray-500 mb-5">
      <Link href="/sdk" className="hover:text-gray-900">sdk</Link>
      <span>/</span>
      {lang ? (
        <>
          <Link href="/sdk/api" className="hover:text-gray-900">api</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{lang}</span>
        </>
      ) : (
        <span className="text-gray-900 font-medium">api</span>
      )}
    </div>
  );
}

export function TocLink({ id, label }: { id: string; label: string }) {
  return (
    <li>
      <a href={`#${id}`} className="block text-sm text-gray-600 hover:text-gray-900 py-0.5 font-mono">
        {label}
      </a>
    </li>
  );
}
